import { clearRecordingData, downloadReconstructedAudioById, getAllRecordingUUIDs, getRecordingDataFromDB, RecordingData } from "@/utils/indexedDBUtils";
import { useCallback, useEffect, useState } from "react";
import { subscribeRecordingLocalStateUpdated } from "@/repositories/recordingLocalStateEvents";
import { IndexDBContext, IndexDBContextType } from "./IndexDBContext";

export interface IndexDBProviderProps {
  children: React.ReactNode;
}

export function IndexDBProvider({ children }: IndexDBProviderProps) {
  const [allRecordingUUIDs, setAllRecordingUUIDs] = useState<string[]>([]);
  const [allRecordingData, setAllRecordingData] = useState<RecordingData[]>([]);

  const isOnIndexDB = (recordingId: string) => {
    if (!allRecordingUUIDs.length) return false;

    return allRecordingUUIDs.some((id) => id === recordingId);
  };

  const getIdsFromIndexDB = useCallback(async () => {
    try {
      const allUUIDs = await getAllRecordingUUIDs();
      const recordings = await Promise.all(allUUIDs.map((id) => getRecordingDataFromDB(id)));
      setAllRecordingUUIDs(allUUIDs);
      setAllRecordingData(recordings.filter(Boolean) as RecordingData[]);
    } catch (error) {
      console.error("Error getting audio from IndexedDB:", error);
    }
  }, []);

  const deleteFromIndexDB = async (ids: string[]) => {
    try {
      const validIds = ids.filter((id) => typeof id === "string" && id.trim().length > 0);

      const deletePromises = validIds.map(async (recordingId) => {
        await clearRecordingData(recordingId);
      });

      await Promise.all(deletePromises);

      await getIdsFromIndexDB();
    } catch (error) {
      console.error("Error deleting audio from IndexedDB:", error);
      throw error;
    }
  };

  const downloadAudioFromIndexedDB = async (recordingId: string) => {
    await getRecordingDataFromDB(recordingId).then(async (data) => {
      const recordingData = JSON.parse(data?.data || "{}");
      let downloadFileName = recordingId;
      const normalizeFileNamePart = (value: string) =>
        value
          .trim()
          .replace(/[\s/:,]+/g, "-")
          .replace(/-+/g, "-");

      const meetingName = typeof recordingData.meetingName === "string" ? recordingData.meetingName : "";
      const meetingDateTimeRaw = recordingData.meetingDateTime || recordingData.meetinDateTime;
      const meetingDateTime = typeof meetingDateTimeRaw === "string" ? meetingDateTimeRaw : "";

      if (meetingName && meetingDateTime) {
        const safeMeetingName = normalizeFileNamePart(meetingName);
        const safeMeetingDateTime = normalizeFileNamePart(meetingDateTime);
        downloadFileName = `${safeMeetingName}_${safeMeetingDateTime}`;
      } else if (meetingName) {
        downloadFileName = normalizeFileNamePart(meetingName);
      }

      await downloadReconstructedAudioById(recordingId, downloadFileName);
    });
  };

  const value: IndexDBContextType = {
    allRecordingUUIDs,
    allRecordingData,
    isOnIndexDB,
    deleteFromIndexDB,
    downloadAudioFromIndexedDB,
    updateState: getIdsFromIndexDB,
  };

  useEffect(() => {
    getIdsFromIndexDB();
  }, [getIdsFromIndexDB]);

  useEffect(() => {
    const unsubscribe = subscribeRecordingLocalStateUpdated(async () => {
      try {
        const allUUIDs = await getAllRecordingUUIDs();
        const recordings = await Promise.all(allUUIDs.map((id) => getRecordingDataFromDB(id)));
        setAllRecordingUUIDs(allUUIDs);
        setAllRecordingData(recordings.filter(Boolean) as RecordingData[]);
      } catch (error) {
        console.error("Error updating recordings from IndexedDB:", error);
      }
    });

    return unsubscribe;
  }, []);

  return <IndexDBContext value={value}>{children}</IndexDBContext>;
}
